import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, Heart, MessageSquare, UserPlus, UserCheck, Send, PhoneMissed } from "lucide-react";
import { api } from "@/lib/api";
import { Avatar } from "@/components/Avatar";
import { useUI } from "@/components/Layout";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { formatDistanceToNow } from "date-fns";

const iconFor = (type) => {
  const map = { like: Heart, comment: MessageSquare, friend_request: UserPlus, friend_accept: UserCheck, message: Send, group_message: MessageSquare, story_reaction: Heart, missed_call: PhoneMissed };
  return map[type] || Bell;
};

export default function NotificationsDropdown() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { startCall } = useUI();
  const [open, setOpen] = useState(false);

  const { data: notes = [] } = useQuery({
    queryKey: ["notifications"],
    queryFn: async () => (await api.get("/notifications")).data,
    refetchInterval: 15000,
  });

  const unread = notes.filter((n) => !n.read).length;

  const onOpenChange = async (v) => {
    setOpen(v);
    if (v && unread > 0) {
      await api.post("/notifications/read");
      qc.invalidateQueries({ queryKey: ["notifications"] });
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <button data-testid="notifications-btn" className="relative w-10 h-10 rounded-full flex items-center justify-center hover:bg-accent transition-colors">
          <Bell className="w-5 h-5" />
          {unread > 0 && (
            <span data-testid="notif-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#E024A3] text-white text-[10px] font-bold flex items-center justify-center">{unread}</span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 bg-popover border-border p-0 max-h-[420px] overflow-y-auto">
        <div className="px-4 py-3 border-b border-border font-heading font-bold">Notifications</div>
        {notes.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-muted-foreground">No notifications yet</div>
        ) : (
          notes.map((n) => {
            const Icon = iconFor(n.type);
            return (
              <button
                key={n.id}
                data-testid={`notification-${n.id}`}
                onClick={() => {
                  if (n.type === "missed_call" && n.actor_id) startCall("direct", n.actor_id);
                  else if (n.link) navigate(n.link);
                  setOpen(false);
                }}
                className={`w-full flex items-start gap-3 px-4 py-3 hover:bg-accent transition-colors text-left ${!n.read ? "bg-[#6C0BA9]/10" : ""}`}
              >
                <div className="relative">
                  <Avatar user={{ name: n.actor_name, avatar_url: n.actor_avatar }} size={38} />
                  <span className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center ${n.type === "missed_call" ? "bg-red-500" : "bg-[#6C0BA9]"}`}>
                    <Icon className="w-3 h-3 text-white" />
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm leading-snug">{n.text}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}
                    {n.type === "missed_call" && <span className="text-[#C084FC] font-medium"> · Tap to call back</span>}
                  </p>
                </div>
              </button>
            );
          })
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
